import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Fingerprint,
  BookOpen,
  FolderOpen,
  Brain,
  Layers,
  MessageSquare,
  Crosshair,
} from 'lucide-react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { resolveGlobalInstructions } from '@/lib/workspace/prompt';
import { DEFAULT_GLOBAL_INSTRUCTIONS } from '@/lib/workspace/types';
import { estimateTokensFromText, formatTokenCount } from '@/lib/workspace/tokens';
import {
  PERSONAS,
  PERSONA_ICONS,
  getPersona,
} from '@/lib/workspace/personas';
import { Toggle } from '@/components/ui/toggle';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── Shared bits ───

function SectionTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-medium text-[#e8e6e3]">{title}</h3>
      {sub && <p className="text-[11px] text-[#5a5a5a] leading-relaxed">{sub}</p>}
    </div>
  );
}

/** Clickable settings row with a Toggle on the right. */
function ToggleRow({
  title,
  sub,
  checked,
  onChange,
  accent,
}: {
  title: string;
  sub: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  accent?: 'default' | 'amber';
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-[#1f1f1f] bg-[#0d0d0d] hover:border-[#2a2a2a] transition-colors cursor-pointer"
    >
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] text-[#e8e6e3] leading-snug">{title}</span>
        <span className="block text-[11px] text-[#5a5a5a] mt-0.5 leading-snug">{sub}</span>
      </span>
      <Toggle checked={checked} onChange={onChange} label={title} accent={accent} />
    </div>
  );
}

/** Live "how prompts are assembled" diagram — collapsible, reflects current state. */
function PromptStackCard() {
  const { settings, activeProject } = useWorkspace();
  const [open, setOpen] = useState(false);

  const persona = getPersona(settings.defaultPersonaId ?? 'default');
  const customGlobal = !!settings.globalInstructions?.trim();
  const identityOn = settings.includeNostrIdentity !== false;

  const rows = useMemo(() => {
    const list: {
      icon: typeof Layers;
      title: string;
      status: string;
      tone?: 'default' | 'amber';
    }[] = [
      {
        icon: Fingerprint,
        title: 'OpenDork session',
        status: identityOn ? 'App rules + your Nostr identity' : 'App rules',
      },
      {
        icon: BookOpen,
        title: 'Global instructions',
        status: customGlobal
          ? `${settings.globalInstructions!.trim().length.toLocaleString()} chars — yours`
          : 'Built-in default',
      },
      {
        icon: PERSONA_ICONS[persona.id],
        title: `Style · ${persona.label}`,
        status:
          persona.id === 'default'
            ? 'Composer picker — Default for new chats'
            : persona.description,
      },
    ];
    if (settings.groundedOnly) {
      list.push({
        icon: Crosshair,
        title: 'Grounded',
        status: 'Project + attachments only',
        tone: 'amber',
      });
    }
    list.push(
      {
        icon: FolderOpen,
        title: 'Project instructions',
        status: activeProject?.instructions?.trim()
          ? `“${activeProject.name}” — ${activeProject.instructions.trim().length.toLocaleString()} chars`
          : 'Stacks inside a project',
      },
      {
        icon: Brain,
        title: 'Project memory',
        status: activeProject?.memoryItems?.length
          ? `${activeProject.memoryItems.length} in “${activeProject.name}”`
          : 'Short facts (pin from chat)',
      },
      {
        icon: Layers,
        title: 'Context pack',
        status: activeProject?.contextItems?.length
          ? `${activeProject.contextItems.length} in “${activeProject.name}”`
          : 'Notes, URLs, Nostr refs',
      },
      {
        icon: MessageSquare,
        title: 'Per message',
        status: 'Thread · Nostr packs · @mentions · attachments',
      },
    );
    return list;
  }, [settings, activeProject, persona, customGlobal, identityOn]);

  return (
    <div className="rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-3.5 py-3 text-left hover:bg-[#141414] transition-colors"
      >
        <span className="flex-1 text-[12px] font-medium text-[#8a8884]">
          How prompts are assembled
        </span>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-[#4a4a4a]" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-[#4a4a4a]" />
        )}
      </button>
      {open && (
        <ol className="px-3.5 pb-3.5 pt-0.5 space-y-2 border-t border-[#1a1a1a]">
          {rows.map((r, i) => {
            const Icon = r.icon;
            return (
              <li key={r.title} className="flex items-center gap-3 pt-2">
                <span className="w-4 shrink-0 text-[10px] text-[#3a3a3a] tabular-nums text-right">
                  {i + 1}
                </span>
                <Icon
                  className={cn(
                    'w-3.5 h-3.5 shrink-0',
                    r.tone === 'amber' ? 'text-amber-400/80' : 'text-[#5a5a5a]',
                  )}
                />
                <span
                  className={cn(
                    'text-[12px] shrink-0',
                    r.tone === 'amber' ? 'text-amber-200/90' : 'text-[#c8c6c3]',
                  )}
                >
                  {r.title}
                </span>
                <span className="ml-auto text-[11px] text-[#4a4a4a] truncate min-w-0 pl-2 text-right">
                  {r.status}
                </span>
              </li>
            );
          })}
          <li className="pt-2 text-[10px] text-[#3a3a3a] leading-relaxed">
            The model never receives your nsec. Toggle layers per chat from the
            composer context strip.
          </li>
        </ol>
      )}
    </div>
  );
}

// ─── Panel ───

/**
 * Settings → Instructions: workspace-wide system prompt + style/Nostr defaults.
 * Artifact-first: your instructions textarea leads; everything saves instantly.
 */
export function GlobalInstructionsPanel() {
  const { settings, setGlobalInstructions, updateSettings } = useWorkspace();
  const [draft, setDraft] = useState(settings.globalInstructions ?? '');
  const [savedFlash, setSavedFlash] = useState(false);
  const dirtyRef = useRef(false);
  const flashTimer = useRef<number | undefined>(undefined);

  // Sync in external changes (sync pull, import) when not mid-edit
  useEffect(() => {
    if (!dirtyRef.current) {
      setDraft(settings.globalInstructions ?? '');
    }
  }, [settings.globalInstructions]);

  // Instant-apply: debounce writes while typing
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = window.setTimeout(() => {
      void setGlobalInstructions(draft).then(() => {
        dirtyRef.current = false;
        setSavedFlash(true);
        window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1600);
      });
    }, 700);
    return () => window.clearTimeout(t);
  }, [draft, setGlobalInstructions]);

  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  const flushNow = () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    void setGlobalInstructions(draft);
  };

  const effective = resolveGlobalInstructions({ globalInstructions: draft });
  const usingDefault = !draft.trim();
  const tokens = estimateTokensFromText(effective);

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <p className="text-[13px] text-[#e8e6e3] font-medium">Global instructions</p>
        <p className="text-[12px] text-[#5a5a5a] leading-relaxed">
          Applied to <span className="text-[#8a8884]">every chat</span> — freestanding and
          inside projects. Saves as you type.
        </p>
      </div>

      {/* 1. The artifact */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] text-[#5a5a5a]">Your instructions</label>
          <span className="text-[10px] text-[#3a3a3a] tabular-nums">
            {draft.length.toLocaleString()} chars · ~{formatTokenCount(tokens)} tokens
            {usingDefault ? ' · built-in default active' : ''}
          </span>
        </div>
        <textarea
          value={draft}
          onChange={(e) => {
            dirtyRef.current = true;
            setDraft(e.target.value);
          }}
          onBlur={flushNow}
          rows={10}
          placeholder={DEFAULT_GLOBAL_INSTRUCTIONS}
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-3.5 py-2.5 text-sm text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a] resize-y min-h-[180px] leading-relaxed transition-colors"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-[#3a3a3a] leading-relaxed pr-3">
            Tone, language, and formatting belong here; project rules belong on the
            project page.
          </p>
          <span className="flex items-center gap-3 shrink-0">
            {savedFlash && (
              <span className="inline-flex items-center gap-1 text-[11px] text-green-400/80">
                <Check className="w-3 h-3" />
                Saved
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                dirtyRef.current = true;
                setDraft('');
              }}
              disabled={usingDefault}
              className="text-[12px] text-[#5a5a5a] hover:text-[#e8e6e3] disabled:opacity-30 transition-colors"
            >
              Clear to default
            </button>
          </span>
        </div>
      </div>

      {/* 2. Single live preview */}
      <div className="rounded-xl border border-[#1f1f1f] bg-[#0a0a0a] px-3.5 py-3">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wider text-[#3a3a3a]">
            Will inject into every chat
          </p>
          {usingDefault && (
            <span className="text-[10px] text-[#5a5a5a]">built-in default</span>
          )}
        </div>
        <p className="text-[12px] text-[#5a5a5a] leading-relaxed line-clamp-4 whitespace-pre-wrap">
          {effective}
        </p>
      </div>

      {/* 3. Defaults for new chats */}
      <div className="space-y-2.5">
        <SectionTitle
          title="Defaults for new chats"
          sub="Also adjustable per chat from the composer style picker — per-chat choices override these."
        />
        <Select
          ariaLabel="Default response style"
          value={settings.defaultPersonaId ?? 'default'}
          onChange={(v) => void updateSettings({ defaultPersonaId: v })}
          options={PERSONAS.map((p) => {
            const Icon = PERSONA_ICONS[p.id];
            return {
              value: p.id,
              label: p.label,
              description: p.description,
              icon: <Icon className="w-4 h-4" />,
            };
          })}
        />
        <ToggleRow
          title="Grounded by default"
          sub="Prefer project memory, context pack, and attachments — less invention"
          checked={!!settings.groundedOnly}
          onChange={(on) => void updateSettings({ groundedOnly: on })}
          accent="amber"
        />
      </div>

      {/* 4. Nostr in prompts */}
      <div className="space-y-2.5">
        <SectionTitle
          title="Nostr in prompts"
          sub="A short session block tells the model this is a browser workspace and, optionally, who you are. Public profile fields only — never your nsec."
        />
        <ToggleRow
          title="Include my Nostr identity in prompts"
          sub="npub + profile metadata so “my profile” works without pasting keys"
          checked={settings.includeNostrIdentity !== false}
          onChange={(on) => void updateSettings({ includeNostrIdentity: on })}
        />
        {settings.includeNostrIdentity !== false && (
          <Select
            ariaLabel="Identity detail"
            value={settings.nostrIdentityDetail ?? 'standard'}
            onChange={(v) =>
              void updateSettings({
                nostrIdentityDetail: v as 'minimal' | 'standard' | 'full',
              })
            }
            options={[
              { value: 'minimal', label: 'Minimal', description: 'npub only' },
              {
                value: 'standard',
                label: 'Standard',
                description: 'name, NIP-05, about',
              },
              {
                value: 'full',
                label: 'Full',
                description: '+ relay list sample',
              },
            ]}
          />
        )}
        <ToggleRow
          title="Auto-fetch recent notes for @mentions"
          sub="When on, @someone pulls profile + recent public notes (budgeted)"
          checked={settings.mentionAutoFetchNotes !== false}
          onChange={(on) => void updateSettings({ mentionAutoFetchNotes: on })}
        />
      </div>

      {/* 5. Live prompt stack */}
      <PromptStackCard />
    </div>
  );
}
